import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  AppLayoutMode,
  AppLayoutSurface,
  BreakpointKey,
  BREAKPOINTS,
  getBreakpointKey,
  getLayoutSurface,
  isDesktopViewport,
  isMobileViewport,
  isTabletViewport,
  resolveAppLayoutMode,
} from '../config/breakpoints';
import { isNativeApp } from '../utils/platformUtils';

export interface AppLayoutState {
  /** Mode shell : mobile (app-like) ou desktop. */
  layout: AppLayoutMode;
  surface: AppLayoutSurface;
  breakpoint: BreakpointKey;
  width: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isNative: boolean;
}

const AppLayoutContext = createContext<AppLayoutState | null>(null);

function readViewportWidth(): number {
  if (typeof window === 'undefined') return BREAKPOINTS.md;
  return window.innerWidth;
}

function buildLayoutState(width: number): AppLayoutState {
  const isNative = isNativeApp();
  const layout = resolveAppLayoutMode(width, isNative);
  const surface = getLayoutSurface(width);

  return {
    layout,
    surface,
    breakpoint: getBreakpointKey(width),
    width,
    isMobile: isMobileViewport(width) || isNative,
    isTablet: isTabletViewport(width) && !isNative,
    isDesktop: isDesktopViewport(width) && !isNative,
    isNative,
  };
}

export const AppLayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AppLayoutState>(() => buildLayoutState(readViewportWidth()));

  const syncLayout = useCallback(() => {
    setState(buildLayoutState(readViewportWidth()));
  }, []);

  useEffect(() => {
    syncLayout();
    window.addEventListener('resize', syncLayout);
    return () => window.removeEventListener('resize', syncLayout);
  }, [syncLayout]);

  useEffect(() => {
    document.documentElement.dataset.appLayout = state.layout;
    document.documentElement.dataset.appSurface = state.surface;
    if (state.isNative) {
      document.documentElement.dataset.appNative = 'true';
    } else {
      delete document.documentElement.dataset.appNative;
    }
  }, [state.layout, state.surface, state.isNative]);

  const value = useMemo(() => state, [state]);

  return <AppLayoutContext.Provider value={value}>{children}</AppLayoutContext.Provider>;
};

export function useAppLayout(): AppLayoutState {
  const ctx = useContext(AppLayoutContext);
  if (!ctx) {
    throw new Error('useAppLayout must be used within AppLayoutProvider');
  }
  return ctx;
}

/** Variante tolérante (hors provider) — évite les crashs dans du code legacy. */
export function useAppLayoutOptional(): AppLayoutState {
  const ctx = useContext(AppLayoutContext);
  return ctx ?? buildLayoutState(readViewportWidth());
}
